import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Phone, CalendarCheck, UserPlus, ShoppingBag, Ticket, Repeat,
  Target, Radio, Gift, ShieldCheck, Sparkles, ArrowRight, Check, Building2,
  Wand2, PenTool, LineChart, MessageSquare, Compass, Megaphone,
  Tv, Newspaper, MonitorPlay, Minus, Footprints,
  Plane, TramFront, Building, Dumbbell, Trophy, ShoppingCart, Fuel,
} from "lucide-react";
import { MarketingPage } from "../../components/smartreach/ChromeClassic";
import SignalMap from "../../components/smartreach/SignalMap";

// Content carried over from the redesigned homepage
const SIGNALS = [
  { tag: "SIGNAL 01 — T+0:00", h: "Detected at competitor", p: "A prospect's device is identified inside a competing location's geofence, in real time." },
  { tag: "SIGNAL 02 — T+0:06", h: "Targeted in the moment", p: "They're served your offer while they're still deciding where to spend — not weeks later." },
  { tag: "SIGNAL 03 — T+0:41", h: "Visit confirmed", p: "SmartReachAI verifies the walk-in at your location and reports it back. Attribution, not assumption." },
];
const WHY = [
  { n: "01", h: "Conquest the customers already in-market", p: "Target people standing at your competitor's location right now — not a cold audience that searched a keyword three weeks ago." },
  { n: "02", h: "Prove ROI in the metric that matters", p: "Foot-traffic attribution shows exactly how many targeted visits became real, in-store visits — not just clicks." },
  { n: "03", h: "Compete without a national budget", p: "Built for single-location and regional operators, not agencies managing million-dollar national campaigns." },
];

const OUTCOMES = [
  { icon: MapPin, label: "Store visits" }, { icon: Phone, label: "Phone calls" },
  { icon: CalendarCheck, label: "Bookings" }, { icon: UserPlus, label: "Qualified leads" },
  { icon: ShoppingBag, label: "Sales" }, { icon: Ticket, label: "Event turnout" },
  { icon: Repeat, label: "Repeat customers" },
];
const STEPS = ["Understand", "Diagnose", "Recommend", "Build", "Approve", "Activate", "Learn", "Grow"];
const INDUSTRIES = [
  { slug: "restaurants", label: "Restaurants" }, { slug: "retail", label: "Retail" },
  { slug: "auto", label: "Auto" }, { slug: "home-services", label: "Home Services" },
  { slug: "entertainment-events", label: "Entertainment & Events" }, { slug: "professional-services", label: "Professional Services" },
  { slug: "doctors", label: "Doctors & Medical" },
];

// Premium environments (representative brand-safe inventory, honestly labeled)
const ENVIRONMENTS = [
  { title: "Connected TV & Streaming", note: "Representative premium streaming environments",
    items: [
      { name: "Disney+", slug: "disneyplus", color: "#113CCF" }, { name: "Netflix", slug: "netflix", color: "#E50914" },
      { name: "Prime Video", slug: "primevideo", color: "#00A8E1" }, { name: "Hulu", slug: "hulu", color: "#1CE783" },
      { name: "Peacock", slug: null, color: "#000000" }, { name: "Max", slug: null, color: "#002BE7" },
      { name: "Pluto TV", slug: null, color: "#1A1A5C" }, { name: "Tubi", slug: null, color: "#6C00E5" },
      { name: "Roku", slug: "roku", color: "#662D91" }, { name: "Fire TV", slug: null, color: "#FF9900" },
      { name: "Samsung TV Plus", slug: "samsung", color: "#1428A0" }, { name: "LG Channels", slug: "lg", color: "#A50034" },
      { name: "NBCUniversal", slug: null, color: "#6E55DC" },
    ] },
  { title: "Premium Websites & Trusted Publishers", note: "Representative premium publisher environments",
    items: [
      { name: "The New York Times", slug: null, color: "#000000" }, { name: "Bloomberg", slug: "bloomberg", color: "#000000" },
      { name: "Forbes", slug: "forbes", color: "#000000" }, { name: "The Guardian", slug: "theguardian", color: "#052962" },
      { name: "Realtor.com", slug: null, color: "#D92228" }, { name: "AARP", slug: null, color: "#E4002B" },
      { name: "Experian", slug: "experian", color: "#26478D" }, { name: "Kantar", slug: null, color: "#00A1E0" },
    ] },
  { title: "Social & Search Activation", note: "Integrated social and search platforms",
    items: [
      { name: "Facebook", slug: "facebook", color: "#1877F2" }, { name: "Instagram", slug: "instagram", color: "#E4405F" },
      { name: "Google", slug: "google", color: "#4285F4" }, { name: "YouTube", slug: "youtube", color: "#FF0000" },
      { name: "TikTok", slug: "tiktok", color: "#000000" }, { name: "LinkedIn", slug: "linkedin", color: "#0A66C2" },
      { name: "X", slug: "x", color: "#000000" },
    ] },
  { title: "Digital Out-of-Home", note: "Digital screen placements in public environments", dooh: true,
    items: [
      { name: "Billboards", icon: MonitorPlay }, { name: "Airports", icon: Plane }, { name: "Transit Hubs", icon: TramFront },
      { name: "Office Buildings", icon: Building2 }, { name: "Elevator Networks", icon: Building }, { name: "Gyms", icon: Dumbbell },
      { name: "Stadiums", icon: Trophy }, { name: "Live Event Venues", icon: Ticket }, { name: "Malls", icon: ShoppingBag },
      { name: "Grocery Stores", icon: ShoppingCart }, { name: "Gas Stations", icon: Fuel },
    ] },
];

const Brand = ({ item }) => {
  const mono = (
    <span style={{ width: 20, height: 20, borderRadius: 5, background: item.color || "var(--navy)", color: "#fff",
      display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
      {item.name.replace(/^The /, "")[0]}
    </span>
  );
  return (
    <div className="sr-env-chip" data-testid={`env-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, border: "1px solid var(--border)",
        borderRadius: 999, padding: "6px 12px 6px 8px", background: "#fff", fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>
      {item.icon ? <item.icon size={16} color="var(--blue)" />
        : item.slug ? (
          <span style={{ position: "relative", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <img src={`https://cdn.simpleicons.org/${item.slug}`} alt="" width={18} height={18} style={{ display: "block" }}
              onError={(e) => { e.currentTarget.style.display = "none"; const m = e.currentTarget.nextSibling; if (m) m.style.display = "inline-flex"; }} />
            {React.cloneElement(mono, { style: { ...mono.props.style, display: "none" } })}
          </span>
        ) : mono}
      {item.name}
    </div>
  );
};

// Honest capability comparison vs the walled gardens (no vendor/DSP names)
const COMPARE = [
  { f: "Reaches customers beyond search results & social feeds", us: true, ppc: false, meta: false },
  { f: "Contextual targeting — reach people by what they're reading or watching, no cookies", us: true, ppc: "partial", meta: false },
  { f: "Target & measure around exact competitor locations", us: true, ppc: false, meta: false },
  { f: "Foot-traffic analysis — tie ads to real store visits", us: true, ppc: false, meta: "partial" },
  { f: "Streaming TV, online audio, native & digital out-of-home", us: true, ppc: "partial", meta: false },
  { f: "You keep & own your customer data and results", us: true, ppc: false, meta: false },
  { f: "Measured on outcomes (visits, calls, sales) — not just clicks", us: true, ppc: "partial", meta: "partial" },
];

const Cell = ({ v }) => (
  v === true ? <Check size={18} color="var(--success)" />
    : v === "partial" ? <Minus size={16} color="var(--gold,#F2B632)" />
      : <span style={{ color: "var(--muted)", fontSize: 18, lineHeight: 1 }}>—</span>
);


const Section = ({ eyebrow, title, sub, children, style }) => (
  <section className="sr-section" style={style}>
    <div className="sr-wrap">
      {eyebrow && <div className="sr-eyebrow">{eyebrow}</div>}
      {title && <h2 style={{ fontSize: 34, marginTop: 8, maxWidth: 720 }}>{title}</h2>}
      {sub && <p className="sr-muted" style={{ marginTop: 12, maxWidth: 680, fontSize: 17 }}>{sub}</p>}
      <div style={{ marginTop: 32 }}>{children}</div>
    </div>
  </section>
);

export default function HomeClassic() {
  return (
    <MarketingPage>
      {/* Hero */}
      <div className="sr-hero">
        <div className="sr-wrap" style={{ padding: "84px 24px 90px" }}>
          <span className="sr-badge sr-hero-badge"><Sparkles size={13} /> AI local growth, done for you</span>
          <h1 style={{ marginTop: 18, maxWidth: 760 }}>Turn local attention into customers.</h1>
          <p style={{ marginTop: 18 }}>
            Tell SmartReachAI your goal and budget. It builds and runs a local growth plan across digital,
            radio, offers, and customer follow-up—then shows what produced visits, leads, and sales.
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 30, flexWrap: "wrap" }}>
            <Link to="/signup" className="sr-btn sr-btn-gold" data-testid="hero-build-plan-btn">
              Build my growth plan <ArrowRight size={16} />
            </Link>
            <Link to="/signup" className="sr-btn sr-btn-ghost" data-testid="hero-strategist-btn"
              style={{ background: "transparent", color: "#fff", borderColor: "rgba(255,255,255,.3)" }}>
              Talk to a strategist
            </Link>
          </div>
        </div>
      </div>

      {/* Foot-traffic attribution (from redesigned view) */}
      <section className="sr-section" style={{ background: "#fff" }} data-testid="tech">
        <div className="sr-wrap">
          <div className="sr-eyebrow">Foot-traffic attribution</div>
          <h2 style={{ fontSize: 34, marginTop: 8, maxWidth: 720 }}>We don&apos;t estimate foot traffic. We measure it — the way Google &amp; Meta can&apos;t.</h2>
          <p className="sr-muted" style={{ marginTop: 12, maxWidth: 680, fontSize: 17 }}>
            SmartReachAI reaches devices seen at competitor points of interest inside your trade area, then measures
            how many of those devices later show up at your location — a real store visit, not a click or an estimate.
            Google and Meta optimize for online activity; for a local business, that never proves who actually walked in.
          </p>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", marginTop: 32 }}>
            {SIGNALS.map((s) => (
              <div key={s.tag} className="sr-card sr-card-hover" data-testid={`signal-${s.tag.match(/\d+/)[0]}`}>
                <div className="sr-eyebrow" style={{ color: "var(--gold,#B97418)", fontSize: 11 }}>{s.tag}</div>
                <h3 style={{ marginTop: 10, fontSize: 18 }}>{s.h}</h3>
                <p className="sr-muted" style={{ marginTop: 8, fontSize: 14 }}>{s.p}</p>
              </div>
            ))}
          </div>
          <div className="sr-card" style={{ marginTop: 20, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <span className="sr-badge sr-badge-gold" data-testid="results-badge">◆ IN-MARKET REACH + MEASURED VISITS = PROVABLE LOCAL ROI</span>
            <p className="sr-muted" style={{ fontSize: 13.5, maxWidth: 440 }}>Reach customers at the moment they&apos;re choosing where to spend, then tie every dollar to confirmed store visits — labeled Observed / Modeled / Verified.</p>
          </div>
        </div>
        <div className="sr-wrap" style={{ marginTop: 28 }}><SignalMap /></div>
      </section>

      {/* Why it matters (from redesigned view) */}
      <Section eyebrow="Why it matters" title="You don't need more reach. You need more customers walking through the door.">
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
          {WHY.map((w) => (
            <div key={w.n} className="sr-card sr-card-hover" data-testid={`why-${w.n}`}>
              <span className="sr-step-num">{w.n}</span>
              <h3 style={{ marginTop: 12, fontSize: 18 }}>{w.h}</h3>
              <p className="sr-muted" style={{ marginTop: 8, fontSize: 14 }}>{w.p}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Outcome cards */}
      <Section eyebrow="Outcomes, not impressions" title="Grow the results your business actually cares about"
        sub="Choose a goal and SmartReachAI builds the plan, runs it, and measures the outcome.">
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" }}>
          {OUTCOMES.map((o) => (
            <div key={o.label} className="sr-pill sr-card-hover" data-testid={`outcome-${o.label.toLowerCase().replace(/\s/g,"-")}`}>
              <span className="sr-pill-ico"><o.icon size={20} /></span>
              <strong style={{ color: "var(--navy)" }}>{o.label}</strong>
            </div>
          ))}
        </div>
      </Section>

      {/* Premium environments — content partners / inventory */}
      <section className="sr-section" style={{ background: "#fff" }} data-testid="premium-environments">
        <div className="sr-wrap">
          <div className="sr-eyebrow">Premium environments</div>
          <h2 style={{ fontSize: 34, marginTop: 8 }}>Not just more impressions — better ones</h2>
          <p className="sr-muted" style={{ marginTop: 12, maxWidth: 680, fontSize: 17 }}>
            Brand-safe, premium inventory across every screen a customer touches.
          </p>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", marginTop: 32 }}>
            {ENVIRONMENTS.map((env) => (
              <div key={env.title} className="sr-card" data-testid={`env-card-${env.title.split(" ")[0].toLowerCase()}`}>
                <h3 style={{ fontSize: 20 }}>{env.title}</h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
                  {env.items.map((it) => <Brand key={it.name} item={it} />)}
                </div>
                <div className="sr-eyebrow" style={{ marginTop: 18, fontSize: 11, opacity: 0.7 }}>{env.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <Section eyebrow="How it works" title="From goal to growth in eight simple moves" style={{ background: "#fff" }}
        sub="A complete draft growth plan in under ten minutes — then you approve and launch.">
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))" }}>
          {STEPS.map((s, i) => (
            <div key={s} className="sr-card sr-card-hover" style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <span className="sr-step-num">{i + 1}</span>
              <strong style={{ color: "var(--navy)" }}>{s}</strong>
            </div>
          ))}
        </div>
      </Section>

      {/* AI at the core */}
      <Section eyebrow="AI at the core" title="A team of AI agents working on your growth"
        sub="SmartReachAI doesn't just place ads — a coordinated set of AI agents plans, writes, buys, measures and follows up. Every action is explainable, and you stay in control of what runs.">
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
          {[
            { icon: Compass, t: "Growth Strategist", d: "Turns your goal & budget into an offer, channel mix, forecast and 90-day calendar — in minutes." },
            { icon: Target, t: "Local Market Analyst", d: "Reads your local demand, seasonality and competitors to find your best opportunity." },
            { icon: PenTool, t: "AI Ad Creation", d: "Writes on-brand ad concepts, headlines, scripts and landing copy across every channel — ready to review.", gold: true },
            { icon: Megaphone, t: "Media Buyer", d: "Optimizes pacing, bids, frequency and daypart inside the limits you approve." },
            { icon: LineChart, t: "Visit & Revenue Analyst", d: "Explains what produced visits, leads and sales — labeled by evidence level." },
            { icon: MessageSquare, t: "Lead Concierge", d: "Answers, qualifies, books and follows up with your leads within your rules and hours." },
          ].map((a) => (
            <div key={a.t} className="sr-card sr-card-hover" data-testid={`ai-agent-${a.t.split(" ")[0].toLowerCase()}`}
              style={a.gold ? { borderColor: "var(--gold)", borderWidth: 2 } : {}}>
              <span className="sr-pill-ico" style={a.gold ? { background: "#FDF3D6", color: "#9A7412" } : { background: "var(--navy)", color: "#fff" }}><a.icon size={20} /></span>
              <h3 style={{ marginTop: 12, fontSize: 18 }}>{a.t}{a.gold && <span className="sr-badge sr-badge-gold" style={{ marginLeft: 8 }}>Popular</span>}</h3>
              <p className="sr-muted" style={{ marginTop: 8, fontSize: 14 }}>{a.d}</p>
            </div>
          ))}
        </div>
        <div className="sr-card" style={{ marginTop: 20, display: "flex", gap: 12, alignItems: "center", background: "var(--navy)", color: "#fff" }}>
          <Wand2 size={22} color="var(--gold)" />
          <p style={{ color: "#cdd8ee", fontSize: 14 }}>
            Every AI recommendation shows <strong style={{ color: "#fff" }}>why</strong>, its expected impact, confidence, and what it needs your approval for.
            AI never changes budget, targeting, creative or launch state without your say-so.
          </p>
        </div>
      </Section>

      {/* Competitor + Visit + Radio */}
      <Section eyebrow="What makes it different" title="Three things generic ad tools can't do for a local business">
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))" }}>
          {[
            { icon: Target, title: "Competitor Conquesting Studio", to: "/competitor-targeting",
              body: "Target and measure around exact competitor storefronts using custom boundaries or the smallest supported radius — shown honestly before you launch." },
            { icon: MapPin, title: "Visit Intelligence", to: "/visit-intelligence",
              body: "See observed, modeled, and verified visits — never guesses dressed up as results. Full methodology and confidence included." },
            { icon: Radio, title: "FunAsia Radio + Digital", to: "/radio-digital",
              body: "Sync owned radio with digital and RadioLife.app offers so on-air moments become measurable visits and redemptions." },
          ].map((c) => (
            <Link to={c.to} key={c.title} className="sr-card sr-card-hover" data-testid={`diff-${c.title.split(" ")[0].toLowerCase()}`}>
              <span className="sr-pill-ico" style={{ background: "var(--navy)", color: "#fff" }}><c.icon size={20} /></span>
              <h3 style={{ marginTop: 14, fontSize: 20 }}>{c.title}</h3>
              <p className="sr-muted" style={{ marginTop: 8 }}>{c.body}</p>
              <span style={{ color: "var(--blue)", fontWeight: 700, marginTop: 12, display: "inline-flex", gap: 6, alignItems: "center" }}>
                Learn more <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      {/* Beyond Google & Meta */}
      <Section eyebrow="Beyond Google & Meta" title="Why this works when Google and Facebook ads don't"
        style={{ background: "#fff" }}
        sub="Search and social ads only reach people already searching or scrolling one feed. Our engine reaches your customers across the whole internet — and proves it drove real visits.">
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", marginBottom: 28 }}>
          {[
            { icon: Newspaper, t: "Contextual targeting", d: "Show up next to the content people are reading and watching right now — no cookies, no waiting to be searched for." },
            { icon: Footprints, t: "Foot-traffic analysis", d: "See how your ads translate into real store visits, labeled by evidence — not just clicks and impressions." },
            { icon: Target, t: "Competitor targeting", d: "Reach shoppers around your competitors' storefronts and win them over — something walled-garden ads simply can't do." },
            { icon: Tv, t: "Streaming TV & audio", d: "Run premium streaming TV, online audio and video ads that Search and Social feeds don't offer local businesses." },
          ].map((c) => (
            <div key={c.t} className="sr-card sr-card-hover" data-testid={`beyond-${c.t.split(" ")[0].toLowerCase()}`}>
              <span className="sr-pill-ico" style={{ background: "#FDF3D6", color: "#9A7412" }}><c.icon size={20} /></span>
              <h3 style={{ marginTop: 12, fontSize: 18 }}>{c.t}</h3>
              <p className="sr-muted" style={{ marginTop: 8, fontSize: 14 }}>{c.d}</p>
            </div>
          ))}
        </div>

        <div className="sr-card" style={{ overflowX: "auto", padding: 0 }} data-testid="compare-table">
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead>
              <tr style={{ background: "var(--navy)", color: "#fff" }}>
                <th style={{ textAlign: "left", padding: "14px 18px", fontSize: 14 }}>What matters for a local business</th>
                <th style={{ padding: "14px 12px", fontSize: 14, width: 130 }}>SmartReachAI</th>
                <th style={{ padding: "14px 12px", fontSize: 14, width: 130, color: "#9fb2d6" }}>Google / PPC</th>
                <th style={{ padding: "14px 12px", fontSize: 14, width: 130, color: "#9fb2d6" }}>Facebook / Meta</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((r, i) => (
                <tr key={r.f} style={{ background: i % 2 ? "var(--blue-light)" : "#fff" }}>
                  <td style={{ padding: "12px 18px", fontSize: 14, color: "var(--navy)" }}>{r.f}</td>
                  <td style={{ textAlign: "center", padding: "12px" }}><Cell v={r.us} /></td>
                  <td style={{ textAlign: "center", padding: "12px" }}><Cell v={r.ppc} /></td>
                  <td style={{ textAlign: "center", padding: "12px" }}><Cell v={r.meta} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="sr-muted" style={{ marginTop: 14, fontSize: 13, display: "flex", flexWrap: "wrap", gap: 18, alignItems: "center" }} data-testid="compare-legend">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Check size={15} color="var(--success)" /> Yes</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Minus size={15} color="var(--gold,#F2B632)" /> Limited</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ color: "var(--muted)", fontWeight: 700 }}>—</span> Not really</span>
        </div>
      </Section>

      {/* RadioLife loop */}
      <Section eyebrow="RadioLife.app" title="An engagement & loyalty loop that brings customers back" style={{ background: "#fff" }}
        sub="Earn → Activate → Redeem → Repeat. RadioLife.app is the consumer radio & engagement app that keeps customers coming back.">
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
          {[
            { icon: Radio, t: "Earn", d: "Listening codes, content, visits, purchases and referrals." },
            { icon: Gift, t: "Activate", d: "Time-bound merchant offers plus a FunAsia reward." },
            { icon: Check, t: "Redeem", d: "QR, POS, code, receipt or staff validation." },
            { icon: Repeat, t: "Repeat", d: "AI triggers replenishment and next-visit journeys." },
          ].map((s) => (
            <div key={s.t} className="sr-card">
              <span className="sr-pill-ico" style={{ background: "#FDF3D6", color: "#9A7412" }}><s.icon size={20} /></span>
              <h3 style={{ marginTop: 12, fontSize: 18 }}>{s.t}</h3>
              <p className="sr-muted" style={{ marginTop: 6, fontSize: 14 }}>{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Service modes */}
      <Section eyebrow="Your call" title="Self-Service, AI-Assisted, or a Professional team">
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
          {[
            { t: "Self-Service", d: "AI builds the plan; you approve and launch." },
            { t: "AI-Assisted", d: "AI builds and optimizes; a specialist reviews exceptions." },
            { t: "Professional Service", d: "An assigned team runs strategy, creative, media & radio." },
          ].map((m, i) => (
            <div key={m.t} className="sr-card sr-card-hover" style={i === 1 ? { borderColor: "var(--blue)", borderWidth: 2 } : {}}>
              {i === 1 && <span className="sr-badge" style={{ marginBottom: 10 }}>Most popular</span>}
              <h3 style={{ fontSize: 20 }}>{m.t}</h3>
              <p className="sr-muted" style={{ marginTop: 8 }}>{m.d}</p>
            </div>
          ))}
        </div>
        <p className="sr-muted" style={{ marginTop: 16, fontSize: 14 }}>
          Switch modes anytime — your campaigns, customers, and results move with you. No migration, no duplication.
        </p>
      </Section>

      {/* Industries */}
      <Section eyebrow="Playbooks" title="Built for local businesses like yours" style={{ background: "#fff" }}>
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
          {INDUSTRIES.map((i) => (
            <Link key={i.slug} to={`/solutions/${i.slug}`} className="sr-card sr-card-hover"
              data-testid={`industry-${i.slug}`} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Building2 size={20} color="var(--blue)" />
              <strong style={{ color: "var(--navy)" }}>{i.label}</strong>
            </Link>
          ))}
        </div>
      </Section>

      {/* Trust */}
      <Section eyebrow="Trust & methodology" title="Honest data. Privacy by design.">
        <div className="sr-card" style={{ display: "flex", gap: 16, alignItems: "flex-start", maxWidth: 820 }}>
          <ShieldCheck size={28} color="var(--success)" style={{ flexShrink: 0 }} />
          <p className="sr-muted">
            We label every number as <strong>Projected</strong>, <strong>Observed</strong>, <strong>Modeled</strong>, or <strong>Verified</strong> —
            with attribution window, match rate, confidence, and limitations. Aggregate reporting only. Sensitive
            categories are blocked. We never guarantee visits or sales, and we never present a projection as a result.
          </p>
        </div>
      </Section>

      {/* Final CTA */}
      <div className="sr-hero">
        <div className="sr-wrap" style={{ padding: "64px 24px", textAlign: "center" }}>
          <h2 style={{ color: "#fff", fontSize: 34 }}>Ready to grow your local business?</h2>
          <p style={{ margin: "12px auto 0" }}>Build a complete growth plan in under ten minutes.</p>
          <Link to="/signup" className="sr-btn sr-btn-gold" style={{ marginTop: 24 }} data-testid="footer-cta-btn">
            Build my growth plan <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </MarketingPage>
  );
}
